import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import {
  ViewList as ListIcon,
  CalendarMonth as CalendarIcon,
  ViewKanban as KanbanIcon,
  Dashboard as DashboardIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  ArrowBack as BackIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  MenuOpen as MenuIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenIcon,
  Article as ContentIcon,
  Google as GoogleIcon,
} from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { vi } from 'date-fns/locale';
import { getUserData, removeUserData } from '../../services/storageService';
import contentService from '../../services/contentService';
import googleAuthService from '../../services/googleAuthService';
import SheetsSetup from './SheetsSetup';

const STATUS_CONFIG = {
  'ĐÃ ĐĂNG': { color: '#10b981', bgColor: '#d1fae5', label: 'Đã đăng' },
  'CHỜ ĐĂNG': { color: '#f59e0b', bgColor: '#fef3c7', label: 'Chờ đăng' },
  'ĐÃ LÊN LỊCH': { color: '#3b82f6', bgColor: '#dbeafe', label: 'Đã lên lịch' },
};

const VIEWS = {
  DASHBOARD: 'dashboard',
  TABLE: 'table',
  CALENDAR: 'calendar',
  KANBAN: 'kanban',
};

const ContentManager = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [user, setUser] = useState(null);
  const [configs, setConfigs] = useState([]); // All connected sheets
  const [config, setConfig] = useState(null); // Current selected sheet
  const [needSetup, setNeedSetup] = useState(false);
  const [googleAuthorized, setGoogleAuthorized] = useState(false);
  const [needGoogleAuth, setNeedGoogleAuth] = useState(false);
  const [activeView, setActiveView] = useState(VIEWS.TABLE);
  const [contents, setContents] = useState([]);
  const [columns, setColumns] = useState([]); // Dynamic columns from sheet
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ search: '' });

  // UI State
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sheetsMenuAnchor, setSheetsMenuAnchor] = useState(null); // For sheets dropdown
  const [selectedContent, setSelectedContent] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // New content form - dynamic based on columns
  const [newContent, setNewContent] = useState({});

  // Check URL params for auth callback
  useEffect(() => {
    const googleAuth = searchParams.get('google_auth');
    const error = searchParams.get('error');
    
    if (googleAuth === 'success') {
      showSnackbar('Kết nối Google thành công!', 'success');
      setGoogleAuthorized(true);
      setNeedGoogleAuth(false);
      // Clear URL params
      setSearchParams({});
    }
    if (error) {
      showSnackbar(`Lỗi: ${error}`, 'error');
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Check authentication and config
  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate('/login', { replace: true });
      return;
    }
    if (userData.role !== 'admin' && 
        (!userData.departmentRoles || !userData.departmentRoles.includes('marketing_dept'))) {
      navigate('/department-selection', { replace: true });
      return;
    }
    setUser(userData);
    checkGoogleAuth();
  }, [navigate]);

  const checkGoogleAuth = async () => {
    try {
      const result = await googleAuthService.getStatus();
      if (result.success && result.authorized) {
        setGoogleAuthorized(true);
        setNeedGoogleAuth(false);
        checkConfig();
      } else {
        setGoogleAuthorized(false);
        setNeedGoogleAuth(true);
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking Google auth:', error);
      setNeedGoogleAuth(true);
      setLoading(false);
    }
  };

  const checkConfig = async () => {
    try {
      const result = await contentService.getConfigs();
      if (result.success && result.data && result.data.length > 0) {
        setConfigs(result.data);
        // Set current config to default or first one
        const defaultConfig = result.data.find(c => c.isDefault) || result.data[0];
        setConfig(defaultConfig);
        setNeedSetup(false);
      } else {
        setConfigs([]);
        setNeedSetup(true);
        setSetupOpen(true);
      }
    } catch (error) {
      console.error('Error checking config:', error);
      if (error.response?.data?.needAuth) {
        setNeedGoogleAuth(true);
      } else {
        setNeedSetup(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSheet = (selectedConfig) => {
    setConfig(selectedConfig);
    setSheetsMenuAnchor(null);
  };

  const handleDeleteSheet = async (configId) => {
    if (!window.confirm('Bạn có chắc muốn xóa kết nối này?')) return;
    try {
      await contentService.deleteConfig(configId);
      showSnackbar('Đã xóa kết nối', 'success');
      // Refresh configs
      const result = await contentService.getConfigs();
      if (result.success) {
        setConfigs(result.data);
        if (result.data.length > 0) {
          const newDefault = result.data.find(c => c.isDefault) || result.data[0];
          setConfig(newDefault);
        } else {
          setConfig(null);
          setNeedSetup(true);
        }
      }
    } catch (error) {
      showSnackbar('Lỗi khi xóa', 'error');
    }
  };

  const handleSetDefaultSheet = async (configId) => {
    try {
      await contentService.setDefaultConfig(configId);
      showSnackbar('Đã đặt làm mặc định', 'success');
      // Refresh configs
      const result = await contentService.getConfigs();
      if (result.success) {
        setConfigs(result.data);
      }
    } catch (error) {
      showSnackbar('Lỗi', 'error');
    }
  };

  const handleGoogleAuth = async () => {
    try {
      await googleAuthService.authorize();
    } catch (error) {
      showSnackbar('Không thể kết nối Google', 'error');
    }
  };

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!config) return;
    
    setLoading(true);
    try {
      const [contentResult, statsResult] = await Promise.all([
        contentService.getContentData(filters, config._id),
        contentService.getStats(),
      ]);

      if (contentResult.success) {
        setContents(contentResult.data);
        // Update columns from response or use from config
        if (contentResult.columns) {
          setColumns(contentResult.columns);
        } else if (config.columns) {
          setColumns(config.columns);
        }
      }
      if (statsResult.success) setStats(statsResult.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Lỗi khi tải dữ liệu', 'error');
    } finally {
      setLoading(false);
    }
  }, [config, filters]);

  useEffect(() => {
    if (config) fetchData();
  }, [config, fetchData]);

  // Handlers
  const handleSetupComplete = (newConfig) => {
    setConfigs(prev => [...prev, newConfig]);
    setConfig(newConfig);
    setNeedSetup(false);
    setSetupOpen(false);
    showSnackbar('Đã kết nối Google Sheets!', 'success');
  };

  const handleEditContent = (content) => {
    setSelectedContent(content);
    setEditModalOpen(true);
  };

  const handleSaveContent = async () => {
    if (!selectedContent) return;
    try {
      await contentService.updateContent(selectedContent.rowIndex, selectedContent);
      showSnackbar('Cập nhật thành công!', 'success');
      setEditModalOpen(false);
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi cập nhật', 'error');
    }
  };

  const handleCreateContent = async () => {
    // Check if at least one field has value
    const hasValue = Object.values(newContent).some(v => v && v.toString().trim());
    if (!hasValue) {
      showSnackbar('Vui lòng nhập ít nhất một trường', 'error');
      return;
    }
    try {
      await contentService.addContent(newContent);
      showSnackbar('Thêm content thành công!', 'success');
      setCreateModalOpen(false);
      setNewContent({});
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi thêm content', 'error');
    }
  };

  // Render dynamic form field based on column type
  const renderFormField = (col, value, onChange, fullWidth = true) => {
    const commonProps = {
      fullWidth,
      label: col.name,
      value: value || '',
      onChange: (e) => onChange(col.key, e.target.value),
      size: 'small',
      sx: { mb: 2 },
    };

    switch (col.type) {
      case 'date':
        return (
          <TextField
            {...commonProps}
            type="date"
            InputLabelProps={{ shrink: true }}
          />
        );
      case 'time':
        return (
          <TextField
            {...commonProps}
            placeholder="HH:MM"
          />
        );
      case 'number':
        return (
          <TextField
            {...commonProps}
            type="number"
          />
        );
      case 'url':
        return (
          <TextField
            {...commonProps}
            placeholder="https://..."
          />
        );
      case 'textarea':
        return (
          <TextField
            {...commonProps}
            multiline
            rows={3}
          />
        );
      case 'select':
        return (
          <TextField
            {...commonProps}
            placeholder="Nhập giá trị"
          />
        );
      default:
        return (
          <TextField {...commonProps} />
        );
    }
  };

  const handleDeleteContent = async (rowIndex) => {
    if (!window.confirm('Bạn có chắc muốn xóa content này?')) return;
    try {
      await contentService.deleteContent(rowIndex);
      showSnackbar('Xóa thành công!', 'success');
      fetchData();
    } catch (error) {
      showSnackbar('Lỗi khi xóa', 'error');
    }
  };

  const handleLogout = () => {
    removeUserData();
    navigate('/login', { replace: true });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Menu items
  const menuItems = [
    { id: VIEWS.DASHBOARD, icon: DashboardIcon, text: 'Dashboard' },
    { id: VIEWS.TABLE, icon: ListIcon, text: 'Danh sách' },
    { id: VIEWS.CALENDAR, icon: CalendarIcon, text: 'Lịch đăng' },
    { id: VIEWS.KANBAN, icon: KanbanIcon, text: 'Kanban' },
  ];

  // Drawer content
  const drawer = (
    <Box sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)' }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
          Content Social
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
          Google Sheets Integration
        </Typography>
      </Box>
      <Divider />
      <List sx={{ px: 1.5, py: 2, flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.id}
            onClick={() => { setActiveView(item.id); setMobileOpen(false); }}
            sx={{
              borderRadius: 2.5,
              mb: 0.75,
              py: 1.25,
              bgcolor: activeView === item.id ? 'rgba(6,182,212,0.1)' : 'transparent',
              border: activeView === item.id ? '1px solid rgba(6,182,212,0.2)' : '1px solid transparent',
              '&:hover': { backgroundColor: 'rgba(6,182,212,0.08)' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <item.icon sx={{ color: activeView === item.id ? '#06b6d4' : '#64748b' }} />
            </ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{
                fontWeight: activeView === item.id ? 600 : 500,
                color: activeView === item.id ? '#06b6d4' : '#475569',
              }}
            />
          </ListItem>
        ))}
      </List>
      
      {/* Connected Sheets */}
      {configs.length > 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', mb: 1, display: 'block' }}>
              Sheets đã kết nối ({configs.length})
            </Typography>
            {configs.map((cfg) => (
              <Box
                key={cfg._id}
                sx={{
                  p: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  bgcolor: config?._id === cfg._id ? 'rgba(6,182,212,0.1)' : '#f8fafc',
                  border: config?._id === cfg._id ? '1px solid rgba(6,182,212,0.3)' : '1px solid #e2e8f0',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(6,182,212,0.05)' },
                }}
                onClick={() => handleSelectSheet(cfg)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }} noWrap>
                    {cfg.spreadsheetTitle || 'Untitled'}
                  </Typography>
                  {cfg.isDefault && (
                    <Chip label="Mặc định" size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#06b6d4', color: 'white' }} />
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                    {cfg.sheetName}
                  </Typography>
                  {!cfg.isDefault && (
                    <Tooltip title="Đặt mặc định">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSetDefaultSheet(cfg._id); }}>
                        <SettingsIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Xóa kết nối">
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteSheet(cfg._id); }}>
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setSetupOpen(true)}
          sx={{ 
            borderRadius: 2, 
            mb: 1,
            bgcolor: '#06b6d4',
            '&:hover': { bgcolor: '#0891b2' },
          }}
        >
          Thêm Sheet mới
        </Button>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/marketing-dashboard/tasks')}
          sx={{ borderRadius: 2 }}
        >
          Task Management
        </Button>
      </Box>
    </Box>
  );

  // Render Dashboard
  const renderDashboard = () => (
    <Grid container spacing={3}>
      {/* Stats Cards */}
      <Grid item xs={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Tổng content</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#06b6d4' }}>
              {stats?.total || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Đã đăng</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981' }}>
              {stats?.byStatus?.daDang || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Chờ đăng</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b' }}>
              {stats?.byStatus?.choDang || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={6} md={3}>
        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent>
            <Typography variant="body2" color="text.secondary">Đã lên lịch</Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#3b82f6' }}>
              {stats?.byStatus?.daLenLich || 0}
            </Typography>
          </CardContent>
        </Card>
      </Grid>

      {/* Metrics */}
      <Grid item xs={12}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>Metrics tổng hợp</Typography>
          <Grid container spacing={2}>
            {[
              { label: 'Lượt hiển thị', value: stats?.metrics?.totalViews || 0, color: '#8b5cf6' },
              { label: 'Tiếp cận', value: stats?.metrics?.totalReach || 0, color: '#06b6d4' },
              { label: 'Clicks', value: stats?.metrics?.totalClicks || 0, color: '#f59e0b' },
              { label: 'Likes', value: stats?.metrics?.totalLikes || 0, color: '#ef4444' },
              { label: 'Chia sẻ', value: stats?.metrics?.totalShares || 0, color: '#10b981' },
              { label: 'Bình luận', value: stats?.metrics?.totalComments || 0, color: '#3b82f6' },
            ].map((metric, idx) => (
              <Grid item xs={6} md={2} key={idx}>
                <Box sx={{ textAlign: 'center', p: 2, bgcolor: metric.color + '10', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: metric.color }}>
                    {metric.value.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{metric.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  );

  // Render Table View with Dynamic Columns
  const renderTableView = () => {
    // Limit columns to display (first 6 + actions)
    const displayColumns = columns.slice(0, 6);

    return (
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        {/* Filters */}
        <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', bgcolor: '#f8fafc' }}>
          <TextField
            placeholder="Tìm kiếm..."
            size="small"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            {contents.length} dòng • {columns.length} cột
          </Typography>
        </Box>

        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {displayColumns.map((col) => (
                  <TableCell key={col.key} sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}>
                    {col.name}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ bgcolor: '#f1f5f9', fontWeight: 600 }}>Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contents.map((content) => (
                <TableRow key={content.rowIndex} hover>
                  {displayColumns.map((col) => (
                    <TableCell key={col.key} sx={{ maxWidth: 200 }}>
                      <Typography noWrap variant="body2">
                        {content[col.key] || '-'}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Tooltip title="Chỉnh sửa">
                      <IconButton size="small" onClick={() => handleEditContent(content)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Xóa">
                      <IconButton size="small" color="error" onClick={() => handleDeleteContent(content.rowIndex)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {contents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={displayColumns.length + 1} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">Không có dữ liệu</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // Render Calendar View
  const renderCalendarView = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() - 1)))}>
            Tháng trước
          </Button>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {format(currentMonth, 'MMMM yyyy', { locale: vi })}
          </Typography>
          <Button onClick={() => setCurrentMonth(new Date(currentMonth.setMonth(currentMonth.getMonth() + 1)))}>
            Tháng sau
          </Button>
        </Box>
        <Grid container spacing={1}>
          {['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'].map((day) => (
            <Grid item xs={12 / 7} key={day}>
              <Typography variant="caption" sx={{ fontWeight: 600, textAlign: 'center', display: 'block' }}>
                {day}
              </Typography>
            </Grid>
          ))}
          {days.map((day) => {
            const dayContents = contents.filter((c) => {
              if (!c.ngayDang) return false;
              try {
                const contentDate = parseISO(c.ngayDang);
                return isSameDay(contentDate, day);
              } catch {
                return false;
              }
            });
            return (
              <Grid item xs={12 / 7} key={day.toString()}>
                <Box
                  sx={{
                    minHeight: 80,
                    p: 1,
                    border: '1px solid #e2e8f0',
                    borderRadius: 1,
                    bgcolor: dayContents.length > 0 ? '#f0f9ff' : 'white',
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {format(day, 'd')}
                  </Typography>
                  {dayContents.slice(0, 2).map((c, idx) => (
                    <Chip
                      key={idx}
                      label={c.tieuDe?.substring(0, 15) || 'Content'}
                      size="small"
                      sx={{
                        mt: 0.5,
                        width: '100%',
                        height: 20,
                        fontSize: '0.6rem',
                        bgcolor: STATUS_CONFIG[c.tienDo]?.bgColor || '#f1f5f9',
                      }}
                      onClick={() => handleEditContent(c)}
                    />
                  ))}
                  {dayContents.length > 2 && (
                    <Typography variant="caption" color="text.secondary">
                      +{dayContents.length - 2} more
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  // Render Kanban View
  const renderKanbanView = () => {
    const columns = [
      { id: 'CHỜ ĐĂNG', title: 'Chờ đăng', color: '#f59e0b' },
      { id: 'ĐÃ LÊN LỊCH', title: 'Đã lên lịch', color: '#3b82f6' },
      { id: 'ĐÃ ĐĂNG', title: 'Đã đăng', color: '#10b981' },
    ];

    return (
      <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2 }}>
        {columns.map((col) => (
          <Paper
            key={col.id}
            sx={{
              minWidth: 300,
              maxWidth: 300,
              borderRadius: 3,
              bgcolor: '#f8fafc',
            }}
          >
            <Box sx={{ p: 2, borderBottom: `3px solid ${col.color}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {col.title}
              </Typography>
              <Chip
                label={contents.filter((c) => c.tienDo === col.id).length}
                size="small"
                sx={{ bgcolor: col.color, color: 'white', mt: 0.5 }}
              />
            </Box>
            <Box sx={{ p: 1, maxHeight: 500, overflowY: 'auto' }}>
              {contents
                .filter((c) => c.tienDo === col.id)
                .map((content) => (
                  <Card
                    key={content.rowIndex}
                    sx={{ mb: 1, cursor: 'pointer' }}
                    onClick={() => handleEditContent(content)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {content.tieuDe || 'Chưa có tiêu đề'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {content.kenhDang && <Chip label={content.kenhDang} size="small" />}
                        {content.ngayDang && (
                          <Chip label={content.ngayDang} size="small" variant="outlined" />
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
            </Box>
          </Paper>
        ))}
      </Box>
    );
  };

  if (!user) return null;

  // Google Auth Required Screen
  if (needGoogleAuth && !googleAuthorized) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        }}
      >
        <Paper sx={{ p: 5, maxWidth: 500, textAlign: 'center', borderRadius: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4285f4 0%, #34a853 50%, #fbbc05 75%, #ea4335 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <GoogleIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Kết nối Google
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Để sử dụng tính năng Content Social, bạn cần kết nối tài khoản Google 
            để truy cập Google Sheets.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleAuth}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #4285f4 0%, #1a73e8 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a73e8 0%, #1557b0 100%)',
              },
            }}
          >
            Đăng nhập với Google
          </Button>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="text"
              onClick={() => navigate('/marketing-dashboard/tasks')}
              sx={{ color: 'text.secondary' }}
            >
              Quay lại Task Management
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (needSetup && !config) {
    return (
      <SheetsSetup
        open={true}
        onClose={() => navigate('/marketing-dashboard')}
        onComplete={handleSetupComplete}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(248,250,252,0.98) 100%)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          color: '#1e293b',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <ContentIcon sx={{ color: '#06b6d4', mr: 1 }} />
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700, color: '#06b6d4' }}>
            Content Social
          </Typography>

          {!isMobile && (
            <ToggleButtonGroup
              value={activeView}
              exclusive
              onChange={(e, v) => v && setActiveView(v)}
              size="small"
              sx={{ mr: 2 }}
            >
              <ToggleButton value={VIEWS.TABLE}><ListIcon sx={{ mr: 0.5 }} /> List</ToggleButton>
              <ToggleButton value={VIEWS.CALENDAR}><CalendarIcon sx={{ mr: 0.5 }} /> Calendar</ToggleButton>
              <ToggleButton value={VIEWS.KANBAN}><KanbanIcon sx={{ mr: 0.5 }} /> Kanban</ToggleButton>
            </ToggleButtonGroup>
          )}

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
            sx={{ mr: 2, bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
          >
            Thêm
          </Button>

          <IconButton onClick={fetchData} sx={{ mr: 1 }}>
            <RefreshIcon />
          </IconButton>

          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 36, height: 36, bgcolor: '#06b6d4' }}>
              {user.fullName?.[0]}
            </Avatar>
          </IconButton>

          <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            <MenuItem><AccountIcon sx={{ mr: 1 }} /> {user.fullName}</MenuItem>
            <MenuItem onClick={handleLogout}><LogoutIcon sx={{ mr: 1 }} /> Đăng xuất</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box component="nav" sx={{ width: { md: 260 }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 260 } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: 260, mt: '64px', height: 'calc(100% - 64px)' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: '64px', width: { md: 'calc(100% - 260px)' } }}>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        <Container maxWidth="xl" disableGutters>
          {activeView === VIEWS.DASHBOARD && renderDashboard()}
          {activeView === VIEWS.TABLE && renderTableView()}
          {activeView === VIEWS.CALENDAR && renderCalendarView()}
          {activeView === VIEWS.KANBAN && renderKanbanView()}
        </Container>
      </Box>

      {/* Edit Modal - Dynamic Form */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Chỉnh sửa dữ liệu</DialogTitle>
        <DialogContent>
          {selectedContent && columns.length > 0 && (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {columns.map((col) => (
                <Grid item xs={col.type === 'textarea' ? 12 : 6} key={col.key}>
                  {renderFormField(
                    col,
                    selectedContent[col.key],
                    (key, value) => setSelectedContent({ ...selectedContent, [key]: value })
                  )}
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleSaveContent} sx={{ bgcolor: '#06b6d4' }}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Modal - Dynamic Form */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Thêm dòng mới</DialogTitle>
        <DialogContent>
          {columns.length > 0 ? (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {columns.map((col) => (
                <Grid item xs={col.type === 'textarea' ? 12 : 6} key={col.key}>
                  {renderFormField(
                    col,
                    newContent[col.key],
                    (key, value) => setNewContent({ ...newContent, [key]: value })
                  )}
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              Không có thông tin cột. Vui lòng kết nối lại Google Sheets.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModalOpen(false)}>Hủy</Button>
          <Button variant="contained" onClick={handleCreateContent} sx={{ bgcolor: '#06b6d4' }}>
            Thêm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Setup Dialog */}
      <SheetsSetup
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onComplete={handleSetupComplete}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentManager;
